/**
 * UserAvatar Web Component
 *
 * Reusable user avatar component with automatic fallback to default avatar.
 * Handles image loading errors gracefully.
 *
 * Usage:
 *   <user-avatar photo="https://example.com/photo.jpg" alt="John Doe"></user-avatar>
 *   <user-avatar photo="${user.photoURL}" alt="${user.displayName}" size="large"></user-avatar>
 *
 * Attributes:
 *   - photo: Image URL (falls back to default avatar if missing or fails to load)
 *   - alt: Alt text for accessibility (default: "User Avatar")
 *   - size: "small" | "medium" | "large" | "xlarge" (default: "medium")
 *
 * Architecture Note:
 *   Part of Phase 1 Web Components architecture.
 *   Follows asset reusability principles - single source for avatar fallback.
 */

// Imported (not referenced by string) so Vite includes it in the build
// output. A string-literal path would be caught by the SPA rewrite and
// served as index.html, breaking the fallback.
import defaultAvatarUrl from '../assets/images/placeholders/default-avatar.svg';
import { safeImageUrl } from '../utils/escape-html.js';
import './UserAvatar.css';

export class UserAvatar extends HTMLElement {
    static get observedAttributes() {
        return ['photo', 'alt', 'size'];
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        // Avoid unnecessary re-renders when a parent re-applies the same
        // attributes (common when auth state fires multiple times). Each
        // re-render destroys + recreates the <img>, forcing a fresh fetch
        // and a brief flash of alt text.
        if (oldValue === newValue) return;
        this.render();
    }

    render() {
        // Built with DOM APIs rather than an innerHTML template on purpose.
        // Callers escape these values before setting them as attributes, but
        // the parser decodes entities on the way in — so getAttribute() hands
        // back the raw string. Re-interpolating it into innerHTML would undo
        // the caller's escaping and turn a stored profile field into markup.
        const alt = this.getAttribute('alt') || 'User Avatar';
        const size = this.getAttribute('size') || 'medium';
        const photo = safeImageUrl(this.getAttribute('photo')) || this.getDefaultAvatar();

        // Size variants (matches design system)
        const sizeMap = {
            small: '24',
            medium: '32',
            large: '48',
            xlarge: '64',
        };

        const dimension = sizeMap[size] || sizeMap.medium;

        const img = document.createElement('img');
        img.className = 'user-avatar';
        img.src = photo;
        img.alt = alt;
        img.width = Number(dimension);
        img.height = Number(dimension);
        img.dataset.size = size;
        // referrerpolicy=no-referrer helps Google's profile-photo CDN serve
        // a cacheable response; sending a Referer header can trigger stricter
        // caching behavior and intermittent failures.
        img.referrerPolicy = 'no-referrer';
        img.loading = 'eager';
        img.decoding = 'async';

        // Add error handler for fallback
        img.addEventListener('error', () => {
            if (img.src !== this.getDefaultAvatar()) {
                img.src = this.getDefaultAvatar();
            }
        });

        this.replaceChildren(img);
    }

    getDefaultAvatar() {
        return defaultAvatarUrl;
    }
}

// Register the custom element
customElements.define('user-avatar', UserAvatar);
